import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Tab,
    Tabs,
    Box,
    TextField,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert,
    AlertTitle,
    CircularProgress,
    MenuItem,
    Paper,
    Stack,
    useTheme,
    alpha,
    IconButton,
    Divider,
    Card,
    CardContent
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import Guidelines from '../Guidlines/Guidelines';
import { addRatingData, addUserRating, updateUserRating, deleteUserRating } from '../../Queries';
import { getCachedUserRatingResults, clearUserRatingCache } from '../../../utils/cachedQueries';
import StarIcon from '@mui/icons-material/Star';
import PsychologyIcon from '@mui/icons-material/Psychology';
import WorkIcon from '@mui/icons-material/Work';
import CodeIcon from '@mui/icons-material/Code';
import MenuBookIcon from '@mui/icons-material/MenuBook';

const RatingPopup = ({ open, name, userEmail, onClose, questions, status, form, isMentorMode = false, userRatingData = null, onUpdate = null }) => {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);
    const userRole = localStorage.getItem("userRole");
    const currentUser = localStorage.getItem("user");
    
    // Debug props
    console.log('RatingPopup props:', { open, name, userEmail, isMentorMode, userRatingData, userRole, currentUser });
    const [isLoading, setIsLoading] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isEditMode, setIsEditMode] = useState(form?.isEditMode || false);
    const [existingUserRating, setExistingUserRating] = useState(null);
    
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Updated arrays to match RatingForm structure
    const learningGrowthFields = questions.learning_skill_growth || [];
    const taskOwnershipFields = questions.task_ownership_delivery || [];
    const problemSolvingFields = questions.problem_solving_thinking || [];
    const codeQualityFields = questions.code_quality_engineering || [];
    const communicationFields = questions.communication_collaboration || [];
    const professionalHabitsFields = questions.professional_habits || [];

    const [alert, setAlert] = useState({
        open: false,
        severity: 'success',
        message: '',
    });

    // Updated state structure for category-wise ratings
    const [selfLearningGrowth, setSelfLearningGrowth] = useState({
        rating: '',
        comments: '',
        questions: learningGrowthFields
    });

    const [selfTaskOwnership, setSelfTaskOwnership] = useState({
        rating: '',
        comments: '',
        questions: taskOwnershipFields
    });

    const [selfProblemSolving, setSelfProblemSolving] = useState({
        rating: '',
        comments: '',
        questions: problemSolvingFields
    });

    const [selfCodeQuality, setSelfCodeQuality] = useState({
        rating: '',
        comments: '',
        questions: codeQualityFields
    });

    const [selfCommunication, setSelfCommunication] = useState({
        rating: '',
        comments: '',
        questions: communicationFields
    });

    const [selfProfessionalHabits, setSelfProfessionalHabits] = useState({
        rating: '',
        comments: '',
        questions: professionalHabitsFields
    });

    const [mentorLearningGrowth, setMentorLearningGrowth] = useState({
        comments: '',
        questions: learningGrowthFields
    });

    const [mentorTaskOwnership, setMentorTaskOwnership] = useState({
        comments: '',
        questions: taskOwnershipFields
    });

    const [mentorProblemSolving, setMentorProblemSolving] = useState({
        comments: '',
        questions: problemSolvingFields
    });

    const [mentorCodeQuality, setMentorCodeQuality] = useState({
        comments: '',
        questions: codeQualityFields
    });

    const [mentorCommunication, setMentorCommunication] = useState({
        comments: '',
        questions: communicationFields
    });

    const [mentorProfessionalHabits, setMentorProfessionalHabits] = useState({
        comments: '',
        questions: professionalHabitsFields
    });

    // Removed mentor overall comment state

    const disableUser = () => {
        if (isMentorMode || status === "Closed") {
            return true; // Disable user fields in mentor mode or when form is closed
        }
        if (userRole === "User" || userRole === "Student") {
            return false; // Enable user fields for users/students
        }
        else {
            return true; // Disable user fields for mentors in normal mode
        }
    }

    const disableMentor = () => {
        if (isMentorMode) {
            return false; // Enable mentor fields in mentor mode
        }
        if (userRole === "Mentor" && status !== "Closed") {
            return false; // Enable mentor fields for mentors when form is open
        }
        else {
            return true; // Disable mentor fields for users or when form is closed
        }
    }

    const updateData = (dataArray, userId, updatedData) => {
        if (dataArray === "") {
            dataArray = [];
        }
        else {
            dataArray = JSON.parse(dataArray);
        }
        
        const existingUserIndex = dataArray.findIndex(item => item.user_id === userId);
        
        if (existingUserIndex !== -1) {
            dataArray[existingUserIndex] = updatedData;
        } else {
            dataArray.push(updatedData);
        }
        
        return dataArray;
    };

    const isSubmitDisabled = (role) => {
        let categories = [
            mentorLearningGrowth, 
            mentorTaskOwnership, 
            mentorProblemSolving, 
            mentorCodeQuality, 
            mentorCommunication, 
            mentorProfessionalHabits
        ];
        
        if (role === "User") {
            categories = [
                selfLearningGrowth, 
                selfTaskOwnership, 
                selfProblemSolving, 
                selfCodeQuality, 
                selfCommunication, 
                selfProfessionalHabits
            ];
        }

        console.log('isSubmitDisabled check for role:', role, 'categories:', categories);

        var result = categories.some(category => {
            const isEmpty = role === "User" ?
                category.rating === '' || category.comments === ''
                :
                category.comments === '';
            console.log('Category check:', category, 'isEmpty:', isEmpty);
            return isEmpty;
        });
        console.log('Submit disabled result:', result);
        return result;
    };

    const resetSelfRatingState = () => {
        setSelfLearningGrowth({
            rating: '',
            comments: '',
            questions: learningGrowthFields
        });

        setSelfTaskOwnership({
            rating: '',
            comments: '',
            questions: taskOwnershipFields
        });

        setSelfProblemSolving({
            rating: '',
            comments: '',
            questions: problemSolvingFields
        });

        setSelfCodeQuality({
            rating: '',
            comments: '',
            questions: codeQualityFields
        });

        setSelfCommunication({
            rating: '',
            comments: '',
            questions: communicationFields
        });

        setSelfProfessionalHabits({
            rating: '',
            comments: '',
            questions: professionalHabitsFields
        });
    };

    const resetMentorRatingState = () => {
        console.log('RESETTING mentor rating state - userRole:', userRole, 'isMentorMode:', isMentorMode);
        setMentorLearningGrowth({
            comments: '',
            questions: learningGrowthFields
        });

        setMentorTaskOwnership({
            comments: '',
            questions: taskOwnershipFields
        });

        setMentorProblemSolving({
            comments: '',
            questions: problemSolvingFields
        });

        setMentorCodeQuality({
            comments: '',
            questions: codeQualityFields
        });

        setMentorCommunication({
            comments: '',
            questions: communicationFields
        });

        setMentorProfessionalHabits({
            comments: '',
            questions: professionalHabitsFields
        });
    };

    const handleSuccess = (message) => {
        setAlert({
            open: true,
            severity: 'success',
            message,
        });
        setIsLoading(false);
    };

    const handleError = (message) => {
        setAlert({
            open: true,
            severity: 'error',
            message,
        });
        setIsLoading(false);
    };

    const handleCloseAlert = () => {
        setAlert({
            ...alert,
            open: false,
        });
        // Close the popup after success
        onClose();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        try {
            setIsLoading(true);
            
            if (userRole === "User" || userRole === "Student") {
                // Handle user/student rating submission
                
                // Preserve existing mentor comments if updating an existing rating
                let existingMentorComments = null;
                let existingMentorData = {};
                if (existingUserRating) {
                    try {
                        const existingJson = existingUserRating.json?.S || existingUserRating.json;
                        const existingData = JSON.parse(existingJson);
                        existingMentorComments = existingData.mentor_comments || null;
                        existingMentorData = {
                            mentor_reviewed_by: existingData.mentor_reviewed_by,
                            mentor_reviewed_at: existingData.mentor_reviewed_at
                        };
                    } catch (error) {
                        console.warn('Error parsing existing rating data:', error);
                    }
                }
                
                const userRatingData = {
                    id: existingUserRating ? (existingUserRating.id?.S || existingUserRating.id) : `${Date.now()}`,
                    parent_id: form.form_id.toString(),
                    user_id: userEmail,
                    json: JSON.stringify({
                        ratings: {
                            learning_skill_growth: selfLearningGrowth,
                            task_ownership_delivery: selfTaskOwnership,
                            problem_solving_thinking: selfProblemSolving,
                            code_quality_engineering: selfCodeQuality,
                            communication_collaboration: selfCommunication,
                            professional_habits: selfProfessionalHabits
                        },
                        // Preserve existing mentor comments and metadata
                        ...(existingMentorComments && { mentor_comments: existingMentorComments }),
                        ...existingMentorData
                    }),
                    questions: JSON.stringify(questions),
                    created_at: existingUserRating ? (existingUserRating.created_at?.S || existingUserRating.created_at) : new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    created_by: existingUserRating ? (existingUserRating.created_by?.S || existingUserRating.created_by) : userEmail
                };
                
                console.log('User rating data with preserved mentor comments:', userRatingData);
                
                let response;
                if (existingUserRating) {
                    // Update existing rating
                    userRatingData.id = existingUserRating.id?.S || existingUserRating.id;
                    response = await updateUserRating(userRatingData);
                } else {
                    // Add new rating
                    response = await addUserRating(userRatingData);
                }
                
                if (response.data && response.data.statusCode === 200) {
                    let result = JSON.parse(response.data.body);
                    resetSelfRatingState();
                    // Clear cache for this user to force refresh
                    clearUserRatingCache(userEmail);
                    const successMessage = existingUserRating ? 
                        (result.message || 'Self-reflection updated successfully!') : 
                        (result.message || 'Self-reflection submitted successfully!');
                    handleSuccess(successMessage);
                } else {
                    const errorMessage = existingUserRating ? 
                        'Unable to update self-reflection. Please try again.' : 
                        'Unable to submit self-reflection. Please try again.';
                    handleError(errorMessage);
                }
            } else if (userRole === "Mentor" || isMentorMode) {
                // Handle mentor rating submission - store comments in user form
                const mentorComments = {
                    learning_skill_growth: mentorLearningGrowth,
                    task_ownership_delivery: mentorTaskOwnership,
                    problem_solving_thinking: mentorProblemSolving,
                    code_quality_engineering: mentorCodeQuality,
                    communication_collaboration: mentorCommunication,
                    professional_habits: mentorProfessionalHabits
                };
                
                console.log('Saving mentor comments:', mentorComments);
                
                if (existingUserRating) {
                    // Update user rating with mentor comments
                    const existingJson = existingUserRating.json?.S || existingUserRating.json;
                    const existingData = JSON.parse(existingJson);
                    
                    const updatedData = {
                        ...existingData,
                        mentor_comments: mentorComments,
                        mentor_reviewed_by: currentUser,
                        mentor_reviewed_at: new Date().toISOString()
                    };
                    
                    console.log('Mentor submission - Original data:', existingData);
                    console.log('Mentor submission - Mentor comments being added:', mentorComments);
                    console.log('Mentor submission - Final updated data:', updatedData);
                    
                    const updatedUserRating = {
                        id: existingUserRating.id?.S || existingUserRating.id,
                        parent_id: existingUserRating.parent_id?.S || existingUserRating.parent_id,
                        user_id: existingUserRating.user_id?.S || existingUserRating.user_id,
                        json: JSON.stringify(updatedData),
                        questions: existingUserRating.questions?.S || existingUserRating.questions,
                        // Removed mentor_comment field
                        updated_at: new Date().toISOString(),
                        created_by: existingUserRating.created_by?.S || existingUserRating.created_by
                    };
                    
                    console.log('Mentor submission - Sending to updateUserRating:', updatedUserRating);
                    const response = await updateUserRating(updatedUserRating);
                    
                    if (response.data && response.data.statusCode === 200) {
                        let result = JSON.parse(response.data.body);
                        console.log('Mentor submission - API response:', result);
                        resetMentorRatingState();
                        // Clear cache for this user to force refresh
                        clearUserRatingCache(userEmail);
                        const hasMentorComments = (() => {
                            try {
                                const jsonData = existingUserRating?.json?.S || existingUserRating?.json;
                                return jsonData && JSON.parse(jsonData).mentor_comments;
                            } catch (error) {
                                return false;
                            }
                        })();
                        
                        const mentorSuccessMessage = hasMentorComments ? 
                            (result.message || 'Mentor comments updated successfully!') : 
                            (result.message || 'Mentor comments submitted successfully!');
                        handleSuccess(mentorSuccessMessage);
                        
                        // Don't immediately refresh parent - let user continue working
                        // Parent will refresh when popup closes
                    } else {
                        const hasMentorComments = (() => {
                            try {
                                const jsonData = existingUserRating?.json?.S || existingUserRating?.json;
                                return jsonData && JSON.parse(jsonData).mentor_comments;
                            } catch (error) {
                                return false;
                            }
                        })();
                        
                        const mentorErrorMessage = hasMentorComments ? 
                            'Unable to update mentor comments. Please try again.' : 
                            'Unable to submit mentor comments. Please try again.';
                        handleError(mentorErrorMessage);
                    }
                } else {
                    handleError('No user reflections found to add mentor comments.');
                }
            }
        } catch (error) {
            console.error('Error submitting rating:', error);
            handleError('Unable to submit self-reflection. Please try again.');
        }
    }

    const validateFormData = async () => {
        console.log('validateFormData called with isMentorMode:', isMentorMode, 'userRatingData:', userRatingData);
        try {
            let currentUserRating = null;
            
            // If in mentor mode, use the provided userRatingData
            if (isMentorMode && userRatingData) {
                currentUserRating = userRatingData;
                console.log('Using provided user rating data (mentor mode):', currentUserRating);
            } else {
                // Get user rating data for regular user mode using cached query
                const userRatings = await getCachedUserRatingResults(userEmail);
                console.log('getCachedUserRatingResults returned:', userRatings);
                console.log('Looking for user:', userEmail, 'form:', form.form_id);
                
                // Handle the case where userRatings might have userRatingResults property
                const ratingsArray = userRatings.userRatingResults || userRatings;
                console.log('Ratings array:', ratingsArray);
                
                currentUserRating = ratingsArray.find(rating => {
                    const ratingUserId = rating.user_id?.S || rating.user_id;
                    const ratingParentId = rating.parent_id?.S || rating.parent_id;
                    console.log('Checking rating:', ratingUserId, ratingParentId, 'vs', userEmail, form.form_id.toString());
                    return ratingUserId === userEmail && ratingParentId === form.form_id.toString();
                });
            }
            
            console.log('Found current user rating:', currentUserRating);
            
            if (currentUserRating) {
                setExistingUserRating(currentUserRating);
                const jsonData = currentUserRating.json?.S || currentUserRating.json;
                console.log('Loading existing rating data:', jsonData);
                console.log('Full currentUserRating object:', currentUserRating);
                const ratingData = JSON.parse(jsonData);
                console.log('Parsed rating data:', ratingData);
                console.log('Mentor comments in ratingData:', ratingData.mentor_comments);
                
                // Load user self-assessment data
                if (ratingData.ratings) {
                    const ratings = ratingData.ratings;
                    console.log('Found ratings:', ratings);
                    
                    if (ratings.learning_skill_growth) {
                        console.log('Loading learning_skill_growth:', ratings.learning_skill_growth);
                        setSelfLearningGrowth(prevState => ({
                            ...prevState,
                            rating: ratings.learning_skill_growth.rating || '',
                            comments: ratings.learning_skill_growth.comments || '',
                            questions: ratings.learning_skill_growth.questions || prevState.questions
                        }));
                    }
                    
                    if (ratings.task_ownership_delivery) {
                        setSelfTaskOwnership(prevState => ({
                            ...prevState,
                            rating: ratings.task_ownership_delivery.rating || '',
                            comments: ratings.task_ownership_delivery.comments || '',
                            questions: ratings.task_ownership_delivery.questions || prevState.questions
                        }));
                    }
                    
                    if (ratings.problem_solving_thinking) {
                        setSelfProblemSolving(prevState => ({
                            ...prevState,
                            rating: ratings.problem_solving_thinking.rating || '',
                            comments: ratings.problem_solving_thinking.comments || '',
                            questions: ratings.problem_solving_thinking.questions || prevState.questions
                        }));
                    }
                    
                    if (ratings.code_quality_engineering) {
                        setSelfCodeQuality(prevState => ({
                            ...prevState,
                            rating: ratings.code_quality_engineering.rating || '',
                            comments: ratings.code_quality_engineering.comments || '',
                            questions: ratings.code_quality_engineering.questions || prevState.questions
                        }));
                    }
                    
                    if (ratings.communication_collaboration) {
                        setSelfCommunication(prevState => ({
                            ...prevState,
                            rating: ratings.communication_collaboration.rating || '',
                            comments: ratings.communication_collaboration.comments || '',
                            questions: ratings.communication_collaboration.questions || prevState.questions
                        }));
                    }
                    
                    if (ratings.professional_habits) {
                        setSelfProfessionalHabits(prevState => ({
                            ...prevState,
                            rating: ratings.professional_habits.rating || '',
                            comments: ratings.professional_habits.comments || '',
                            questions: ratings.professional_habits.questions || prevState.questions
                        }));
                    }
                    
                    // Check if user has submitted assessment
                    if (ratings.learning_skill_growth && ratings.learning_skill_growth.rating !== undefined) {
                        setIsFormSubmitted(true);
                    }
                }
                
                // Load mentor comments if available (for both users and mentors)
                console.log('Checking for mentor comments in ratingData for userRole:', userRole, 'isMentorMode:', isMentorMode);
                console.log('Available keys in ratingData:', Object.keys(ratingData));
                console.log('Mentor comments field:', ratingData.mentor_comments);
                
                if (ratingData.mentor_comments) {
                    const mentorComments = ratingData.mentor_comments;
                    console.log('✅ Found mentor comments, loading for userRole:', userRole, 'isMentorMode:', isMentorMode);
                    console.log('Mentor comments data:', mentorComments);
                    
                    if (mentorComments.learning_skill_growth) {
                        console.log('Loading learning_skill_growth mentor comment:', mentorComments.learning_skill_growth);
                        setMentorLearningGrowth(prevState => {
                            console.log('Setting mentorLearningGrowth prevState:', prevState);
                            const newState = {
                                ...prevState,
                                comments: mentorComments.learning_skill_growth.comments || '',
                                questions: mentorComments.learning_skill_growth.questions || prevState.questions
                            };
                            console.log('Setting mentorLearningGrowth newState:', newState);
                            return newState;
                        });
                    }
                    
                    if (mentorComments.task_ownership_delivery) {
                        setMentorTaskOwnership(prevState => ({
                            ...prevState,
                            comments: mentorComments.task_ownership_delivery.comments || '',
                            questions: mentorComments.task_ownership_delivery.questions || prevState.questions
                        }));
                    }
                    
                    if (mentorComments.problem_solving_thinking) {
                        setMentorProblemSolving(prevState => ({
                            ...prevState,
                            comments: mentorComments.problem_solving_thinking.comments || '',
                            questions: mentorComments.problem_solving_thinking.questions || prevState.questions
                        }));
                    }
                    
                    if (mentorComments.code_quality_engineering) {
                        setMentorCodeQuality(prevState => ({
                            ...prevState,
                            comments: mentorComments.code_quality_engineering.comments || '',
                            questions: mentorComments.code_quality_engineering.questions || prevState.questions
                        }));
                    }
                    
                    if (mentorComments.communication_collaboration) {
                        setMentorCommunication(prevState => ({
                            ...prevState,
                            comments: mentorComments.communication_collaboration.comments || '',
                            questions: mentorComments.communication_collaboration.questions || prevState.questions
                        }));
                    }
                    
                    if (mentorComments.professional_habits) {
                        setMentorProfessionalHabits(prevState => ({
                            ...prevState,
                            comments: mentorComments.professional_habits.comments || '',
                            questions: mentorComments.professional_habits.questions || prevState.questions
                        }));
                    }
                } else {
                    console.log('No mentor_comments found in ratingData');
                    console.log('Available keys in ratingData:', Object.keys(ratingData));
                }
                
                // Removed mentor overall comment loading
            }
        } catch (error) {
            console.error('Error validating form data:', error);
        }
        
        // Debug: Log final state after data loading
        console.log('Final mentor states after loading for userRole:', userRole, 'isMentorMode:', isMentorMode);
        setTimeout(() => {
            console.log('mentorLearningGrowth:', mentorLearningGrowth);
            console.log('mentorTaskOwnership:', mentorTaskOwnership);
            console.log('mentorProblemSolving:', mentorProblemSolving);
            console.log('mentorCodeQuality:', mentorCodeQuality);
            console.log('mentorCommunication:', mentorCommunication);
            console.log('mentorProfessionalHabits:', mentorProfessionalHabits);
        }, 500);
    }

    useEffect(() => {
        const initializeForm = async () => {
            console.log('useEffect triggered - Initializing form with edit mode:', form?.isEditMode, 'open:', open, 'isMentorMode:', isMentorMode);
            
            // Reset states first
            setIsFormSubmitted(false);
            setIsEditMode(form?.isEditMode || false);
            setExistingUserRating(null);
            // Removed setMentorOverallComment('');
            
            // Set default tab based on mode
            if (isMentorMode) {
                setTabValue(2); // Mentor Assessment tab
            } else if (userRole === "Mentor") {
                setTabValue(0); // Guidelines tab for mentors
            } else {
                setTabValue(0); // Guidelines tab for users
            }
            
            if (open) {
                console.log('Dialog is open, validating form data... userRole:', userRole, 'isMentorMode:', isMentorMode);
                // Always reset states first to ensure clean state
                resetMentorRatingState();
                resetSelfRatingState();
                // Then load the data which will populate the states
                await validateFormData();
            }
        };
        
        initializeForm();
    }, [open, form?.form_id, isMentorMode])

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose} 
                maxWidth="lg" 
                fullWidth 
                disableBackdropClick
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        maxHeight: '90vh',
                    }
                }}
            >
                {!isLoading && !alert.open ? (
                    <>
                        {/* Enhanced Header */}
                        <Box sx={{ 
                            background: `linear-gradient(135deg, #0a0068 0%, #0c0082 100%)`,
                            color: 'white',
                            position: 'relative',
                            p: 3
                        }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <StarIcon sx={{ fontSize: 28 }} />
                                <Box>
                                    <Typography variant="h6" component="div" fontWeight="600" sx={{ fontSize: '1.1rem' }}>
                                        {isMentorMode ? `Mentor Review - ${name}` : 
                                         isEditMode ? 'Edit Self Reflection' : 'Self Reflection & Rise'}
                                    </Typography>
                                    {existingUserRating && (
                                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                                            {isMentorMode ? 'Review user self-reflection and add mentor comments' :
                                             existingUserRating ? 'Update your comments' : 'Complete your review'}
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                            <IconButton 
                                onClick={onClose} 
                                sx={{ 
                                    position: 'absolute', 
                                    top: 12, 
                                    right: 12,
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: alpha('#ffffff', 0.1)
                                    }
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <DialogContent sx={{ p: 0 }}>
                            {/* Enhanced Tabs */}
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2 }}>
                                <Tabs
                                    value={tabValue}
                                    onChange={handleTabChange}
                                    sx={{
                                        '& .MuiTab-root': {
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '1rem',
                                            '&.Mui-selected': {
                                                color: '#0a0068',
                                            },
                                        },
                                        '& .MuiTabs-indicator': {
                                            backgroundColor: '#0a0068',
                                            height: 3,
                                        },
                                    }}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                >
                                    <Tab 
                                        label="Guidelines" 
                                        icon={<MenuBookIcon />}
                                        iconPosition="start"
                                    />
                                    <Tab 
                                        label="Self Reflection" 
                                        icon={<PsychologyIcon />}
                                        iconPosition="start"
                                    />
                                    <Tab 
                                        label="Mentor Review" 
                                        icon={<WorkIcon />}
                                        iconPosition="start"
                                    />
                                </Tabs>
                            </Box>

                            <Box sx={{ p: 3 }}>
                                <TabPanel value={tabValue} index={0}>
                                    <Guidelines />
                                </TabPanel>
                                
                                <TabPanel value={tabValue} index={1}>
                                    <form>
                                        <SectionFields
                                            activeTab={tabValue}
                                            header="Learning & Skill Growth"
                                            icon={<PsychologyIcon color="primary" />}
                                            fields={learningGrowthFields}
                                            values={selfLearningGrowth}
                                            disabled={disableUser()}
                                            userRole={userRole}
                                            onChange={(field, value) => {
                                                setSelfLearningGrowth(prevState => ({
                                                    ...prevState,
                                                    [field]: value
                                                }));
                                            }}
                                        />

                                        <SectionFields
                                            activeTab={tabValue}
                                            header="Task Ownership & Delivery"
                                            icon={<WorkIcon color="primary" />}
                                            fields={taskOwnershipFields}
                                            values={selfTaskOwnership}
                                            disabled={disableUser()}
                                            userRole={userRole}
                                            onChange={(field, value) => {
                                                setSelfTaskOwnership(prevState => ({
                                                    ...prevState,
                                                    [field]: value
                                                }));
                                            }}
                                        />

                                        <SectionFields
                                            activeTab={tabValue}
                                            header="Problem-Solving & Thinking"
                                            icon={<PsychologyIcon color="primary" />}
                                            fields={problemSolvingFields}
                                            values={selfProblemSolving}
                                            disabled={disableUser()}
                                            userRole={userRole}
                                            onChange={(field, value) => {
                                                setSelfProblemSolving(prevState => ({
                                                    ...prevState,
                                                    [field]: value
                                                }));
                                            }}
                                        />

                                        <SectionFields
                                            activeTab={tabValue}
                                            header="Code Quality & Engineering Practices"
                                            icon={<CodeIcon color="primary" />}
                                            fields={codeQualityFields}
                                            values={selfCodeQuality}
                                            disabled={disableUser()}
                                            userRole={userRole}
                                            onChange={(field, value) => {
                                                setSelfCodeQuality(prevState => ({
                                                    ...prevState,
                                                    [field]: value
                                                }));
                                            }}
                                        />

                                        <SectionFields
                                            activeTab={tabValue}
                                            header="Communication & Collaboration"
                                            icon={<WorkIcon color="primary" />}
                                            fields={communicationFields}
                                            values={selfCommunication}
                                            disabled={disableUser()}
                                            userRole={userRole}
                                            onChange={(field, value) => {
                                                setSelfCommunication(prevState => ({
                                                    ...prevState,
                                                    [field]: value
                                                }));
                                            }}
                                        />

                                        <SectionFields
                                            activeTab={tabValue}
                                            header="Professional Habits"
                                            icon={<WorkIcon color="primary" />}
                                            fields={professionalHabitsFields}
                                            values={selfProfessionalHabits}
                                            disabled={disableUser()}
                                            userRole={userRole}
                                            onChange={(field, value) => {
                                                setSelfProfessionalHabits(prevState => ({
                                                    ...prevState,
                                                    [field]: value
                                                }));
                                            }}
                                        />
                                    </form>
                                </TabPanel>
                                
                                <TabPanel value={tabValue} index={2}>
                                        <form>
                                        <SectionFields
                                            activeTab={tabValue}
                                            header="Learning & Skill Growth"
                                            icon={<PsychologyIcon color="primary" />}
                                            fields={learningGrowthFields}
                                            values={mentorLearningGrowth}
                                            disabled={disableMentor()}
                                            userRole={userRole}
                                            onChange={(field, value) => {
                                                setMentorLearningGrowth(prevState => ({
                                                    ...prevState,
                                                    [field]: value
                                                }));
                                            }}
                                        />

                                        <SectionFields
                                            activeTab={tabValue}
                                            header="Task Ownership & Delivery"
                                            icon={<WorkIcon color="primary" />}
                                            fields={taskOwnershipFields}
                                            values={mentorTaskOwnership}
                                            disabled={disableMentor()}
                                            userRole={userRole}
                                            onChange={(field, value) => {
                                                setMentorTaskOwnership(prevState => ({
                                                    ...prevState,
                                                    [field]: value
                                                }));
                                            }}
                                        />

                                        <SectionFields
                                            activeTab={tabValue}
                                            header="Problem-Solving & Thinking"
                                            icon={<PsychologyIcon color="primary" />}
                                            fields={problemSolvingFields}
                                            values={mentorProblemSolving}
                                            disabled={disableMentor()}
                                            userRole={userRole}
                                            onChange={(field, value) => {
                                                setMentorProblemSolving(prevState => ({
                                                    ...prevState,
                                                    [field]: value
                                                }));
                                            }}
                                        />

                                        <SectionFields
                                            activeTab={tabValue}
                                            header="Code Quality & Engineering Practices"
                                            icon={<CodeIcon color="primary" />}
                                            fields={codeQualityFields}
                                            values={mentorCodeQuality}
                                            disabled={disableMentor()}
                                            userRole={userRole}
                                            onChange={(field, value) => {
                                                setMentorCodeQuality(prevState => ({
                                                    ...prevState,
                                                    [field]: value
                                                }));
                                            }}
                                        />

                                        <SectionFields
                                            activeTab={tabValue}
                                            header="Communication & Collaboration"
                                            icon={<WorkIcon color="primary" />}
                                            fields={communicationFields}
                                            values={mentorCommunication}
                                            disabled={disableMentor()}
                                            userRole={userRole}
                                            onChange={(field, value) => {
                                                setMentorCommunication(prevState => ({
                                                    ...prevState,
                                                    [field]: value
                                                }));
                                            }}
                                        />

                                        <SectionFields
                                            activeTab={tabValue}
                                            header="Professional Habits"
                                            icon={<WorkIcon color="primary" />}
                                            fields={professionalHabitsFields}
                                            values={mentorProfessionalHabits}
                                            disabled={disableMentor()}
                                            userRole={userRole}
                                            onChange={(field, value) => {
                                                setMentorProfessionalHabits(prevState => ({
                                                    ...prevState,
                                                    [field]: value
                                                }));
                                            }}
                                        />
                                        
                                        {/* Removed overall mentor comment section */}
                                    </form>
                                </TabPanel>
                            </Box>
                        </DialogContent>
                        
                        {/* Enhanced Action Buttons */}
                        <DialogActions sx={{ 
                            p: 3, 
                            borderTop: `1px solid ${theme.palette.divider}`,
                            gap: 2
                        }}>
                            {tabValue === 1 && (userRole === "User" || userRole === "Student") && (
                                <Button 
                                    onClick={handleSubmit} 
                                    variant="contained"
                                    size="medium"
                                    disabled={disableUser() || isSubmitDisabled("User") || (isFormSubmitted && !isEditMode)}
                                    sx={{
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        px: 3,
                                        py: 1,
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #0a0068 0%, #0c0082 100%)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #0c0082 0%, #0e009e 100%)',
                                            transform: 'translateY(-1px)',
                                        },
                                        '&:disabled': {
                                            background: '#cfcfcf',
                                            color: '#999'
                                        },
                                        transition: 'all 0.2s ease-in-out'
                                    }}
                                >
                                    {existingUserRating ? 'Update Self Reflection' : 'Submit Self Reflection'}
                                </Button>
                            )}
                            
                            {tabValue === 2 && (userRole === "Mentor" || isMentorMode) && (
                                <Button 
                                    onClick={handleSubmit} 
                                    variant="contained"
                                    size="medium"
                                    disabled={disableMentor()}
                                    sx={{
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        px: 3,
                                        py: 1,
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #0a0068 0%, #0c0082 100%)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #0c0082 0%, #0e009e 100%)',
                                            transform: 'translateY(-1px)',
                                        },
                                        '&:disabled': {
                                            background: '#cfcfcf',
                                            color: '#999'
                                        },
                                        transition: 'all 0.2s ease-in-out'
                                    }}
                                >
                                    {(() => {
                                        try {
                                            const jsonData = existingUserRating?.json?.S || existingUserRating?.json;
                                            const hasExistingComment = jsonData && (JSON.parse(jsonData).mentor_comments || existingUserRating?.mentor_comment);
                                            return hasExistingComment ? 'Update Mentor Comments' : 'Submit Mentor Comments';
                                        } catch (error) {
                                            return 'Submit Mentor Comments';
                                        }
                                    })()}
                                </Button>
                            )}
                            
                            <Button 
                                onClick={onClose} 
                                variant="outlined"
                                size="medium"
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    borderColor: '#0a0068',
                                    color: '#0a0068',
                                    '&:hover': {
                                        borderColor: '#0c0082',
                                        backgroundColor: alpha('#0a0068', 0.04),
                                    }
                                }}
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </>
                ) : (
                    <DialogContent sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        p: 4
                    }}>
                        {isLoading && (
                            <>
                                <CircularProgress size={48} thickness={4} />
                                <Typography variant="body1" color="text.secondary" sx={{ mt: 2, fontSize: '0.85rem' }}>
                                    Saving details...
                                </Typography>
                            </>
                        )}
                        
                        {alert.open && (
                            <Alert 
                                severity={alert.severity} 
                                onClose={handleCloseAlert}
                                sx={{ 
                                    borderRadius: 2,
                                    maxWidth: 400,
                                    '& .MuiAlert-message': {
                                        fontSize: '1rem'
                                    }
                                }}
                            >
                                <AlertTitle sx={{ fontWeight: 'bold' }}>
                                    {alert.severity === 'success' ? 'Success!' : 'Error'}
                                </AlertTitle>
                                {alert.message}
                            </Alert>
                        )}
                    </DialogContent>
                )}
            </Dialog>
        </>
    );
};

// Enhanced Section Fields Component - Category-wise ratings
const SectionFields = ({ header, icon, fields, values, disabled, onChange, activeTab, userRole }) => {
    const theme = useTheme();

    return (
        <Paper 
            elevation={0} 
            sx={{ 
                mb: 3, 
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                borderRadius: 2,
                overflow: 'hidden'
            }}
        >
            <Box sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                p: 2
            }}>
                <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        {icon}
                        <Typography variant="h6" fontWeight="bold" color="primary" sx={{ fontSize: '1rem' }}>
                            {header}
                        </Typography>
                    </Stack>
                    <Stack spacing={1} sx={{ ml: 5 }}>
                        <Typography variant="subtitle2" fontWeight="600" color="text.primary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                            Questions to Consider:
                        </Typography>
                        {fields.map((field, index) => (
                            <Typography key={index} variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                {index + 1}. {field}
                            </Typography>
                        ))}
                    </Stack>
                </Stack>
            </Box>
            
            <Box sx={{ p: 3 }}>
                <Stack direction="column" spacing={2}>
                    {activeTab === 1 && (
                        <TextField
                            label="Category Rating"
                            select
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={values.rating || ''}
                            InputLabelProps={{ style: { fontSize: '0.8rem' } }}
                            InputProps={{ style: { fontSize: '0.8rem' } }}
                            onChange={(e) => onChange('rating', e.target.value)}
                            disabled={disabled}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                }
                            }}
                        >
                            {[0, 1, 2, 3, 4, 5].map((value) => (
                                <MenuItem key={value} value={value}>
                                    {value} - {value === 0 ? 'Needs Improvement' : value === 5 ? 'Excellent' : `Level ${value}`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    
                    {(activeTab === 2 || activeTab === 1) && (
                        <TextField
                            label="Comments"
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            InputLabelProps={{ style: { fontSize: '0.8rem' } }}
                            InputProps={{ style: { fontSize: '0.8rem' } }}
                            value={values.comments || ''}
                            onChange={(e) => onChange('comments', e.target.value)}
                            disabled={disabled}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                }
                            }}
                        />
                    )}
                </Stack>
            </Box>
        </Paper>
    );
}

// Enhanced Tab Panel Component
function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default RatingPopup;